import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { InputTextModule } from "primeng/inputtext";
import { Dropdown, DropdownModule } from "primeng/dropdown";
import { AppSettings } from "../../../../../shared/app.settings";
import { CommonBindingDataService } from "../../../../../shared/services/common-binding-data.service";
import { MessageService } from "primeng/api";
import { EntityService } from "../../../../../shared/services/entity.service";
import { ConfigService } from "app/modules/shared/services/config.service";
import { Countries } from "../../../../../shared/countries";
import { Country } from "../../../../../shared/models/country";
import { UserManagementService } from "app/modules/user-management/services/user-management.service";
import { isValidPhoneNumber } from "libphonenumber-js";

interface CountryOption {
  code: string;
  value: string;
  label: string;
}

interface UserData {
  firstName: string;
  lastName: string;
  email: string;
  contactNumber: string;
  countryCode: string;
  [key: string]: unknown;
}

interface UserFormData extends UserData {
  forTenantCode: string;
  userType: string;
  typeId: string;
  roleIds: string[];
  username: string;
}

interface RoleData {
  roleId: string;
  roleCode: string;
  [key: string]: unknown;
}

interface RoleResponse {
  data: RoleData[];
}

@Component({
  selector: 'app-add-sponsor-user',
  standalone: true,
  imports: [
    BreadcrumbModule,
    DialogModule,
    ButtonModule,
    TranslateModule,
    ReactiveFormsModule,
    InputTextModule,
    DropdownModule,
    FormsModule
  ],
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.scss', '../../../../../shared/components/mi-fields/mobile-input/flags.scss']
})
export class AddSponsorUserComponent implements OnInit {
  @ViewChild('dropdown') dropdown!: Dropdown;
  @Output() closePopup = new EventEmitter<boolean>();
  @Output() saveSuccess = new EventEmitter<boolean>();
  @Input() userId: string;
  @Input() sponsorId: string;

  userForm: FormGroup;
  userHeader: string;
  nextBtnLabel: string;
  previousBtnLabel: string;
  successMessage: string;
  isNew: boolean = true;
  countries: CountryOption[] = [];
  selectedCountry: CountryOption | null = null;
  filterIcon = 'pi pi-search';
  selectedCountryCode: string = '';
  country: Country;
  loadDialogView = false;
  timeZoneCode: string;
  entityDataForRole = {
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: "",
    filters: [],
    countryCode: '',
    deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
    forTenantCode: '',
    actionStatus: '',
    status: 'both',
  };
  sponsorRoleId: string;
  showMobileValidationError: boolean = false;

  constructor(
    private entityService: EntityService,
    public cs: CommonBindingDataService,
    private messageService: MessageService,
    private fb: FormBuilder,
    private configService: ConfigService,
    private userManagementService: UserManagementService

  ) { }

  ngOnInit() {
    this.initializeLabels();
    this.initializeForm();
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    if (this.userId) {
      this.getUserData();
    } else {
      this.setCountries();
      this.loadDialogView = true;
    }

  }

  private initializeLabels() {
    if (this.userId) {
      this.userHeader = this.cs.getLabel("manage_users.lbl_update_user");
      this.nextBtnLabel = this.cs.getLabel("manage_users.lbl_update_user");
      this.successMessage = this.cs.getLabel('manage_users.lbl_user_updated_successfully');
    } else {
      this.userHeader = this.cs.getLabel("manage_users.lbl_invite_user");
      this.nextBtnLabel = this.cs.getLabel("manage_users.lbl_save_user");
      this.successMessage = this.cs.getLabel('manage_users.lbl_user_added_successfully');
    }
    this.previousBtnLabel = this.cs.getLabel("lbl_cancel");
  }

  initializeForm(): void {
    const emailRegex = new RegExp(
      `^(?!.*\\.\\.)` +
      `(?!\\.)` +
      `(?!.*\\.$)` +
      `(?=.{1,64}@)` +
      `(?=.{6,254}$)` +
      `[^\\s@.][^\\s@]*[^\\s@.]` +
      `@` +
      `(?:[a-zA-Z0-9-]+\\.)+` +
      `[a-zA-Z]{2,}$`
    );

    this.userForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.pattern(AppSettings.NAME_PATTERN_WITH_HYPHEN), Validators.maxLength(AppSettings.MAX_LENGTH_FOR_FORM_VALIDATION), Validators.minLength(AppSettings.MIN_LENGTH_FOR_FORM_VALIDATION)]],
      lastName: ['', [Validators.required, Validators.pattern(AppSettings.NAME_PATTERN_WITH_HYPHEN), Validators.maxLength(AppSettings.MAX_LENGTH_FOR_FORM_VALIDATION), Validators.minLength(AppSettings.MIN_LENGTH_FOR_FORM_VALIDATION)]],
      email: ['', [Validators.required, Validators.pattern(emailRegex)]],
      contactNumber: ['', [Validators.pattern(AppSettings.PHONE_PATTERN), Validators.maxLength(AppSettings.MAX_MOBILE_LENGTH), Validators.minLength(AppSettings.MIN_MOBILE_LENGTH)]]
    });
  }

  setCountries() {
    this.countries = Countries.COUNTRY_CODE;
    this.selectedCountry = this.countries.find(country => country.code === this.country[0].countryCode);
    this.getUserRoles();
  }

  onFilter(event: { filter: string }) {
    if (event.filter) {
      this.filterIcon = 'pi pi-times'
    } else {
      this.filterIcon = 'pi pi-search';
    }
  }

  onFilterIconClick() {
    this.filterIcon = 'pi pi-times';
    if (this.dropdown) {
      this.dropdown.filterValue = '';
      this.dropdown.filterValue = '';
      this.dropdown.filterValue = '';
    }
  }

  selectCountry() {
    this.userForm.get('contactNumber').updateValueAndValidity();
    if (this.userForm.controls.contactNumber.value) {
      const isValid = this.validatePhoneNumber(this.selectedCountry?.value, this.userForm.controls.contactNumber.value);
      this.showMobileValidationError = !isValid;
    } else {
      this.showMobileValidationError = false;
    }
    const control = this.userForm.get('contactNumber');
    if (this.showMobileValidationError) {
      control.setErrors({ maxlength: true })
    }
    if (control) {
      control.markAsTouched();
    }
  }

  validatePhoneNumber(countryCode: string, phoneNumber: string): boolean {
    try {
      const formattedCountryCode = countryCode.startsWith('+') ? countryCode : `+${countryCode}`;
      return isValidPhoneNumber(`${formattedCountryCode}${phoneNumber}`);
    } catch (error) {
      return false;
    }
  }

  getUserData() {
    this.userManagementService.getUserDetails(this.userId).subscribe((userData: UserData) => {
      this.userForm.patchValue(userData);
      this.countries = Countries.COUNTRY_CODE;
      if(userData.countryCode) {
        const countryCode = `+${userData.countryCode}`;
        this.selectedCountry = this.countries.find(c => c.value === countryCode);
      } else {
        this.selectedCountry = this.countries.find(c => c.code === this.country[0].countryCode);
      }
      this.loadDialogView = true;
      this.getUserRoles();
    });
  }

  onSaveUser() {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }
    const formData = { ...this.userForm.value };
    formData.countryCode = this.selectedCountry.value.slice(1, this.selectedCountry.value.length);
    formData.forTenantCode = this.configService.getForTenantCode();
    formData.userType = 'sponsor';
    formData.typeId = this.sponsorId;
    formData.roleIds = [this.sponsorRoleId];
    formData.username = `${formData.firstName} ${formData.lastName}`;
    if(!formData.contactNumber) {
      delete formData.contactNumber;
      delete formData.countryCode;
    }
    const userFormData = formData as UserFormData;
    if (this.userId) {
      this.onUpdateUser(userFormData);
    } else {
      this.onAddUser(userFormData);
    }
  }

  onAddUser(userFormData: UserFormData) {
    this.userManagementService.AddNewUser(userFormData).subscribe(() => {
      this.showSuccessMessage('manage_users.message.msg_user_added_successfully');
      this.closeDialog(true);
      this.saveSuccess.emit();
    }, (error: { errors?: { general?: Array<{ message: string }> } }) => {
      const errorMessage = error?.errors?.general?.[0]?.message ?? "An unknown error occurred.";
      this.showErrorMessage(errorMessage);
    });
  }

  onUpdateUser(userFormData: UserFormData) {
    this.userManagementService.EditUser(this.userId, userFormData).subscribe(() => {
      this.showSuccessMessage('manage_users.message.msg_user_update_successfully');
      this.closeDialog(true);
      this.saveSuccess.emit();
    }, (error: { errors?: { general?: Array<{ message: string }> } }) => {
      const errorMessage = error?.errors?.general?.[0]?.message ?? "An unknown error occurred.";
      this.showErrorMessage(errorMessage);
    }
    );
  }

  private showErrorMessage(labelKey: string) {
    this.messageService.add({
      key: 'tst',
      severity: 'error',
      summary: 'Error',
      detail: this.cs.getLabel(labelKey)
    });
  }

  private showSuccessMessage(labelKey: string) {
    this.messageService.add({
      key: 'tst',
      severity: 'success',
      summary: 'Successful',
      detail: this.cs.getLabel(labelKey),
    });
  }

  saveUserData(userData: Record<string, unknown>, isNew: boolean) {
    this.isNew = isNew;
    this.entityService
      .saveAttributeData(userData.entityCode as string, this.userId, userData)
      .subscribe({
        next: () => {
          this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.successMessage });
          setTimeout(() => {
            this.closeDialog(true);
          }, 50);
        },
        error: () => {
          this.messageService.add({ key: 'tst', severity: 'error', summary: 'Error', detail: 'Failed to save user data' });
        }
      });
  }

  onPreviousClick() {
    this.closeDialog(false);
  }

  closeDialog(event: boolean) {
    this.userId = null;
    this.closePopup.emit(event);
  }

  getUserRoles() {
    this.entityDataForRole.countryCode = this.country[0].countryCode;
    this.userManagementService.searchEntity(this.entityDataForRole, false).subscribe((res: RoleResponse) => {
      if (res.data) {
        res.data.forEach(element => {
          if (element.roleCode === AppSettings.ROLE_CODE.SPONSOR) {
            this.sponsorRoleId = element.roleId;
          }
        });
      }
    });
  }
}
